/**
 * Deterministic Search Engine Fallback System
 * Provides comprehensive job search functionality when AI search is unavailable
 * Integrates tokenization, BM25 indexing, and advanced ranking algorithms
 */

import { jobRanker } from './ranker.js';
import { BM25Index } from './bm25.js';
import { tokenize, detectLanguage } from './tokenizer.js';
import { parseQueryToFilters, applyFilters } from '../search.js';

// Search engine configuration
const SEARCH_CONFIG = {
  // Field weights for BM25 scoring
  fieldWeights: {
    title: 3.0,        // Job titles have highest weight
    companyName: 2.0,  // Company names are important
    locations: 2.0,    // Location matching is crucial
    tags: 2.0,         // Skills/tags are highly relevant
    skills: 2.0,       // Technical skills weight
    category: 1.5,     // Job category relevance
    description: 1.0,  // Description content baseline
    requirements: 1.0  // Requirements baseline
  },
  
  // Search behavior settings
  maxResults: 100,
  defaultLimit: 25,
  enableGeoBoost: true,
  enableFreshnessDecay: true,
  debugMode: process.env?.NODE_ENV === 'development'
};

/**
 * Deterministic Search Engine class
 */
class DeterministicSearchEngine {
  constructor() {
    this.isInitialized = false;
    this.lastInitTime = null;
    this.searchStats = {
      totalSearches: 0,
      averageResponseTime: 0,
      popularQueries: new Map(),
      errorCount: 0
    };
  }

  /**
   * Initializes the search engine with job data
   * @param {Array} jobData - Job documents to index
   * @param {Object} options - Configuration options
   */
  async initialize(jobData = [], options = {}) {
    try {
      const startTime = performance.now();
      
      // Merge configuration with options
      const config = { ...SEARCH_CONFIG, ...options };
      
      // Initialize the job ranker with data
      jobRanker?.initialize(jobData, {
        fieldWeights: config?.fieldWeights,
        enableDebugMode: config?.debugMode
      });

      this.isInitialized = true;
      this.lastInitTime = new Date();
      
      const initTime = performance.now() - startTime;
      
      if (config?.debugMode) {
        console.log(`DeterministicSearchEngine initialized in ${initTime?.toFixed(2)}ms`);
        console.log(`Indexed ${jobData?.length} jobs`);
      }

      return {
        success: true,
        indexedJobs: jobData?.length,
        initializationTime: initTime
      };
    } catch (error) {
      console.error('Failed to initialize search engine:', error);
      this.searchStats.errorCount++;
      return {
        success: false,
        error: error?.message
      };
    }
  }

  /**
   * Performs comprehensive job search with fallback indicators
   * @param {string} query - Search query
   * @param {Object} options - Search options and filters
   * @returns {Object} - Search results with fallback indicators
   */
  async search(query = '', options = {}) {
    if (!this.isInitialized) {
      throw new Error('Search engine not initialized. Call initialize() first.');
    }

    const startTime = performance.now();
    
    try {
      // Track search statistics
      this._updateSearchStats(query);

      // Prepare search options with defaults
      const searchOptions = {
        limit: options?.limit || SEARCH_CONFIG?.defaultLimit,
        offset: options?.offset || 0,
        userLocation: options?.userLocation || null,
        filters: options?.filters || {},
        enableGeoBoost: options?.enableGeoBoost ?? SEARCH_CONFIG?.enableGeoBoost,
        enableFreshnessDecay: options?.enableFreshnessDecay ?? SEARCH_CONFIG?.enableFreshnessDecay,
        includeDebugInfo: options?.includeDebugInfo || SEARCH_CONFIG?.debugMode
      };

      // Detect query language for better tokenization
      const queryLanguage = detectLanguage(query);

      // Perform ranking
      const rankingResult = jobRanker?.rankJobs(query, searchOptions);

      // Calculate response time
      const responseTime = performance.now() - startTime;
      
      // Update average response time
      this._updateAverageResponseTime(responseTime);

      // Prepare fallback-enhanced response
      const response = {
        // Core search results
        results: rankingResult?.results || [],
        totalResults: rankingResult?.totalResults || 0,
        
        // Search metadata
        searchTime: responseTime,
        query: {
          original: query,
          language: queryLanguage,
          searchTerms: rankingResult?.query?.searchTerms,
          filters: rankingResult?.query?.filters
        },
        
        // Pagination info
        pagination: rankingResult?.pagination,
        
        // Fallback indicators - CRITICAL for UI
        fallback: {
          mode: 'deterministic',
          reason: 'AI search unavailable',
          capabilities: [
            'keyword matching',
            'location filtering', 
            'date filtering',
            'skill matching',
            'company search',
            'salary filtering'
          ],
          limitations: [
            'no semantic understanding',
            'no natural language parsing',
            'no personalization'
          ]
        },
        
        // Search suggestions
        suggestions: this._generateSearchSuggestions(query, rankingResult?.results),
        
        // Performance metrics
        performance: {
          searchTime: responseTime,
          resultCount: rankingResult?.results?.length,
          indexStats: rankingResult?.debug?.indexStats
        }
      };

      // Add debug information if requested
      if (searchOptions?.includeDebugInfo) {
        response.debug = {
          ...rankingResult?.debug,
          queryLanguage,
          searchEngine: 'deterministic-bm25',
          fieldWeights: SEARCH_CONFIG?.fieldWeights
        };
      }

      return response;
    } catch (error) {
      console.error('Search failed:', error);
      this.searchStats.errorCount++;
      
      return {
        results: [],
        totalResults: 0,
        searchTime: performance.now() - startTime,
        query: { original: query },
        pagination: { limit: searchOptions?.limit || SEARCH_CONFIG?.defaultLimit, offset: 0, hasMore: false },
        fallback: {
          mode: 'error',
          reason: 'Search engine error',
          error: error?.message
        },
        suggestions: []
      };
    }
  }

  /**
   * Updates search statistics for analytics
   * @param {string} query - Search query
   */
  _updateSearchStats(query) {
    this.searchStats.totalSearches++;
    
    if (query?.trim()) {
      const normalizedQuery = query?.toLowerCase()?.trim();
      const currentCount = this.searchStats?.popularQueries?.get(normalizedQuery) || 0;
      this.searchStats?.popularQueries?.set(normalizedQuery, currentCount + 1);
      
      // Keep only top 100 popular queries to prevent memory issues
      if (this.searchStats?.popularQueries?.size > 100) {
        const sortedQueries = Array.from(this.searchStats?.popularQueries?.entries())?.sort(([,a], [,b]) => b - a)?.slice(0, 100);
        this.searchStats.popularQueries = new Map(sortedQueries);
      }
    }
  }

  /**
   * Updates running average response time
   * @param {number} responseTime - Current response time
   */
  _updateAverageResponseTime(responseTime) {
    const totalSearches = this.searchStats?.totalSearches;
    this.searchStats.averageResponseTime = 
      ((this.searchStats?.averageResponseTime * (totalSearches - 1)) + responseTime) / totalSearches;
  }

  /**
   * Generates helpful search suggestions
   * @param {string} query - Original query
   * @param {Array} results - Search results
   * @returns {Array} - Search suggestions
   */
  _generateSearchSuggestions(query, results) {
    const suggestions = [];
    
    // Get suggestions from ranker
    const rankerSuggestions = jobRanker?.getSuggestions(query, results);
    suggestions?.push(...rankerSuggestions);

    // Add popular query suggestions
    const popularQueries = this.getPopularQueries(5);
    popularQueries?.forEach(({ query: popularQuery }) => {
      if (popularQuery !== query?.toLowerCase()?.trim()) {
        suggestions?.push({
          type: 'popular',
          text: popularQuery,
          reason: 'Popular search'
        });
      }
    });

    return suggestions?.slice(0, 6); // Limit to 6 suggestions
  }

  /**
   * Gets popular search queries for analytics
   * @param {number} limit - Number of queries to return
   * @returns {Array} - Popular queries with counts
   */
  getPopularQueries(limit = 10) {
    return Array.from(this.searchStats?.popularQueries?.entries())?.sort(([,a], [,b]) => b - a)?.slice(0, limit)?.map(([query, count]) => ({ query, count }));
  }

  /**
   * Gets comprehensive search engine statistics
   * @returns {Object} - Search engine analytics
   */
  getStats() {
    return {
      isInitialized: this.isInitialized,
      lastInitTime: this.lastInitTime,
      searchStats: {
        totalSearches: this.searchStats?.totalSearches,
        averageResponseTime: Math.round(this.searchStats?.averageResponseTime * 100) / 100,
        errorCount: this.searchStats?.errorCount,
        popularQueriesCount: this.searchStats?.popularQueries?.size
      },
      rankerStats: this.isInitialized ? jobRanker?.getAnalytics() : null,
      configuration: SEARCH_CONFIG
    };
  }

  /**
   * Resets search statistics (useful for testing)
   */
  resetStats() {
    this.searchStats = {
      totalSearches: 0,
      averageResponseTime: 0,
      popularQueries: new Map(),
      errorCount: 0
    };
  }

  /**
   * Checks if search engine is ready for queries
   * @returns {boolean} - Whether engine is ready
   */
  isReady() {
    return this.isInitialized && jobRanker?.isInitialized;
  }

  /**
   * Provides fallback search compatible with existing search interface
   * @param {Array} searchData - Raw search data
   * @param {Object} filters - Search filters
   * @returns {Array} - Filtered and ranked results
   */
  fallbackSearch(searchData, filters) {
    try {
      // Use existing filter logic as backup
      const basicResults = applyFilters(searchData, filters);
      
      // Add fallback indicators to results
      return basicResults?.map(result => ({
        ...result,
        _fallback: true,
        _fallbackMode: 'basic-filter'
      }));
    } catch (error) {
      console.error('Fallback search failed:', error);
      return [];
    }
  }
}

// Create singleton instance
export const deterministicSearchEngine = new DeterministicSearchEngine();

// Export the main search function for easy integration
export const searchJobs = async (query, options = {}) => {
  return await deterministicSearchEngine?.search(query, options);
};

// Export initialization function
export const initializeSearch = async (jobData, options = {}) => {
  return await deterministicSearchEngine?.initialize(jobData, options);
};

// Export utility functions
export { tokenize, detectLanguage, parseQueryToFilters };

// Export classes for advanced usage
export { BM25Index, DeterministicSearchEngine };

export default deterministicSearchEngine;